import React, { useState } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Question, MagnifyingGlass, ChatCircleDots, Lifebuoy, PuzzlePiece } from "@phosphor-icons/react";

const categories = [
  { icon: Lifebuoy, label: "Getting started", articles: ["Create your first script", "Connect your accounts", "Understanding viral scores"] },
  { icon: Question, label: "Billing", articles: ["How credits work", "Upgrade or cancel my plan", "Refunds & invoices"] },
  { icon: ChatCircleDots, label: "AI tools", articles: ["Best niches to start with", "Why hooks matter most", "Caption length on TikTok vs YouTube"] },
];

const extensionSteps = [
  "Open Chrome and go to chrome://extensions",
  "Toggle 'Developer mode' on (top right)",
  "Click 'Load unpacked'",
  "Select the /chrome-extension folder from your ClipForge install",
  "Pin the extension to your toolbar",
  "Click the icon → Settings → paste your ClipForge URL + cf_token from localStorage",
];

const faqs = [
  { q: "How many credits does a script cost?", a: "3 credits per generation. Captions use 2, titles use 1, thumbnails use 5, and voiceovers use 5-10 depending on length." },
  { q: "Can I cancel my subscription anytime?", a: "Yes — cancel from Settings → Billing. You'll keep access until the end of your billing period." },
  { q: "Is the trend data live?", a: "Currently seeded for v1. Live scraping is on our roadmap (Q2 2026)." },
  { q: "Do you offer team accounts?", a: "Coming Q2 2026. For now, Premium covers up to 3 channels under one user." },
];

export default function Help() {
  const [q, setQ] = useState("");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="help-page">
      <Navbar />
      <section className="pt-32 px-6 max-w-5xl mx-auto">
        <div className="text-center">
          <p className="text-[#FF3B6B] text-sm font-mono mb-3">// HELP CENTER</p>
          <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter">How can we help?</h1>
          <div className="relative max-w-xl mx-auto mt-10">
            <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles..."
              className="w-full glass-strong rounded-full px-12 py-4 focus:outline-none focus:border-[#FF3B6B] border border-white/10"
              data-testid="help-search-input"
            />
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
          {categories.map((c, i) => (
            <div key={i} className="glass rounded-xl p-6 hover:border-white/30 transition-all" data-testid={`help-category-${i}`}>
              <c.icon weight="duotone" size={28} className="text-[#FF3B6B]" />
              <h3 className="mt-4 font-semibold">{c.label}</h3>
              <ul className="mt-3 space-y-2 text-sm text-white/70">
                {c.articles.map((a, j) => (
                  <li key={j} className="hover:text-white cursor-pointer">{a}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <h2 className="mt-20 text-3xl font-bold tracking-tighter">Frequently asked</h2>
        <div className="mt-6 space-y-3">
          {faqs.filter(f => !q || f.q.toLowerCase().includes(q.toLowerCase())).map((f, i) => (
            <details key={i} className="glass rounded-xl p-5 group" data-testid={`help-faq-${i}`}>
              <summary className="cursor-pointer flex justify-between items-center">
                <span className="font-medium">{f.q}</span>
                <span className="text-[#FF3B6B] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-white/60 text-sm">{f.a}</p>
            </details>
          ))}
        </div>

        {/* Chrome Extension section */}
        <div className="mt-20 glass-strong rounded-2xl p-8" data-testid="chrome-ext-section">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#FF3B6B]/10 border border-[#FF3B6B]/30 flex items-center justify-center shrink-0">
              <PuzzlePiece weight="duotone" size={24} className="text-[#FF3B6B]" />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold tracking-tighter">Install the Chrome extension</h3>
              <p className="mt-1 text-white/60 text-sm">Surface today's scheduled posts in one click. Copy captions and jump straight to upload pages.</p>
            </div>
          </div>
          <ol className="mt-6 space-y-2 text-sm text-white/70">
            {extensionSteps.map((s, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`ext-step-${i}`}>
                <span className="font-mono text-xs text-[#FF3B6B] mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
          <p className="mt-6 text-xs text-white/50">Extension source ships in <code className="font-mono text-[#00E5FF]">/chrome-extension/</code> of your ClipForge install.</p>
        </div>

        <div className="mt-16 glass-strong rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold tracking-tighter">Still stuck?</h3>
          <p className="mt-2 text-white/60">Email <a href="mailto:support@clipforge.win" className="text-[#FF3B6B]">support@clipforge.win</a> — we reply within a day.</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
